import React, { Dispatch, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Tab, Tabs, toast } from '@node-space/ui-components'
import { NetworkConnectionViewTypes } from 'constants/bvnkNetwork'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useUpdateNetworkConnectionMutation } from 'hooks/mutations/BvnkNetwork/useUpdateNetworkConnectionMutation'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import {
  BvnkNetworkConnection,
  ConnectionStatusType,
  NetworkConnectionViewType,
} from 'types/bvnkNetwork'
import { WalletTypes } from 'types/wallets'
import { getConnectionStatusUpdateSuccessMessage } from 'utils/bvnkNetwork'
import { bvnkNetworkConnectionsAtom } from '../atoms/bvnkNetworkAtoms'
import ActiveConnections from './ActiveConnections'
import IncomingPendingConnections from './IncomingPendingConnections'
import OutgoingPendingConnections from './OutgoingPendingConnections'

interface BvnkNetworkTabsProps {
  queryTab: NetworkConnectionViewType
  activeTab: string
  setActiveTab: Dispatch<React.SetStateAction<string>>
}

const BvnkNetworkTabs = ({ queryTab, activeTab, setActiveTab }: BvnkNetworkTabsProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { profile } = useProfileContext()

  const bvnkNetworkConnections = useAtomValue(bvnkNetworkConnectionsAtom)

  const { mutate: updateNetworkConnection, isPending: isUpdatingConnection } =
    useUpdateNetworkConnectionMutation()

  const { received, requested, connected } = bvnkNetworkConnections ?? {}

  const updateConnection = (
    connection: BvnkNetworkConnection,
    connectionStatus: ConnectionStatusType
  ) => {
    updateNetworkConnection(
      { connectionId: connection?.id, connectionStatus },
      {
        onSuccess: () => {
          const successToastMessage = getConnectionStatusUpdateSuccessMessage(
            connection?.details?.name,
            connectionStatus,
            t
          )
          toast({ header: successToastMessage, variant: 'success' })
          queryClient.invalidateQueries({ queryKey: ['bvnkNetworkConnections'] })
          queryClient.invalidateQueries({ queryKey: reactQueryKeys.walletsV2(WalletTypes.FIAT) })
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.totalBalance(profile?.currencyCode),
          })
        },
        onError: () => {
          toast({
            header: t('bvnkNetwork.updateNetworkConnectionError', {
              networkMember: connection?.details?.name,
            }),
            variant: 'error',
          })
        },
      }
    )
  }

  const tabs: Tab[] = [
    {
      id: NetworkConnectionViewTypes.CONNECTIONS_VIEW,
      testId: 'bvnk-network-connections-tab',
      header: t('bvnkNetwork.connections'),
      content: (
        <ActiveConnections
          key="bvnk-network-active-connections-tab-content"
          activeConnections={connected}
        />
      ),
    },
    {
      id: NetworkConnectionViewTypes.RECEIVED_INVITATIONS_VIEW,
      testId: 'bvnk-network-received-invitations-tab',
      header: t('bvnkNetwork.recievedInvitations'),
      badgeCount: !!received?.length && received?.length,
      content: (
        <IncomingPendingConnections
          key="bvnk-network-incoming-pending-connections-tab-content"
          incomingPendingConnections={received}
          updateConnection={updateConnection}
          isUpdatingConnection={isUpdatingConnection}
        />
      ),
    },
    {
      id: NetworkConnectionViewTypes.SENT_INVITATIONS_VIEW,
      testId: 'bvnk-network-sent-invitations-tab',
      header: t('bvnkNetwork.sentInvitations'),
      badgeCount: !!requested?.length && requested?.length,
      content: (
        <OutgoingPendingConnections
          key="bvnk-network-outgoing-pending-connections-tab-content"
          outgoingPendingConnections={requested}
          updateConnection={updateConnection}
          isUpdatingConnection={isUpdatingConnection}
        />
      ),
    },
  ]

  useEffect(() => {
    if (queryTab) {
      setActiveTab(queryTab)
    }
  }, [queryTab])

  return <Tabs tabs={tabs} defaultTabId={activeTab} />
}

export default BvnkNetworkTabs
